import { ArrowRight, LogIn } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/ui/reveal";
import { Logo } from "./logo";
import type { PublicNavItem } from "./public-layout";

/** The dark band before the footer: one invitation, on every public page. */
export function PublicCta() {
  return (
    <section className="public-cta section-padding relative isolate overflow-hidden text-white">
      <div aria-hidden className="hero-aurora -top-40 left-[10%] bg-[#2f6ff2]" />
      <div aria-hidden className="hero-aurora hero-aurora--b right-[-5%] -bottom-40 bg-[#38bdf8]" />
      <div aria-hidden className="hero-grid" />
      <div className="relative mx-auto max-w-[1440px] px-4 text-center sm:px-8">
        <Reveal from="left" duration={0.8}><p className="section-label section-label--light">はじめる</p></Reveal>
        <Reveal delay={0.12}><h2 className="mt-4 font-display text-[1.75rem] leading-[1.45] font-bold tracking-[0.02em] sm:text-[2.4rem]">先生の知見を、届く言葉に。</h2></Reveal>
        <Reveal delay={0.24}><p className="mx-auto mt-4 max-w-xl text-[15px] leading-[1.9] text-white/75">招待制のサービスです。導入のご相談は、ご案内した担当者までお寄せください。</p></Reveal>
        <Reveal delay={0.36} className="mt-9 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="hero-primary"><Link to="/login"><LogIn />ログイン</Link></Button>
          <Button asChild size="lg" variant="outline" className="hero-secondary"><a href="/#flow">記事ができるまで<ArrowRight /></a></Button>
        </Reveal>
      </div>
    </section>
  );
}

/** The navy footer: the mark and motto, the page's sections, and the small print. */
export function PublicFooter({ nav = [] }: { nav?: readonly PublicNavItem[] }) {
  return (
    <footer className="public-footer text-[13px]">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-4 py-14 sm:px-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <Logo tone="light" />
          <p className="mt-4 font-display text-[15px] text-white/85">専門家の知見を、届く言葉に。</p>
          <p className="mt-2 max-w-sm leading-relaxed text-white/55">士業（税理士・弁護士・社会保険労務士・司法書士・行政書士）の事務所のための AI 記事サービス。一次資料の確認、先生の監修、公開後の改善までを一つの画面で。</p>
        </div>
        {nav.length > 0 && (
          <nav aria-label="ページ内の案内（フッター）">
            <p className="mb-3 text-[11px] font-bold tracking-[0.2em] text-white/45">このページ</p>
            <ul className="space-y-2">
              {nav.map((n) => (
                <li key={n.href}><a href={n.href} className="text-white/80 transition-colors hover:text-sky-300">{n.label}</a></li>
              ))}
            </ul>
          </nav>
        )}
        <div>
          <p className="mb-3 text-[11px] font-bold tracking-[0.2em] text-white/45">ご利用</p>
          <ul className="space-y-2">
            <li><Link to="/login" className="text-white/80 transition-colors hover:text-sky-300">ログイン</Link></li>
            <li><Link to="/forgot-password" className="text-white/80 transition-colors hover:text-sky-300">パスワードをお忘れの方</Link></li>
            <li><Link to="/services" className="text-white/80 transition-colors hover:text-sky-300">サービスのご案内</Link></li>
            <li><Link to="/credits" className="text-white/80 transition-colors hover:text-sky-300">画像の出典</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-2 px-4 py-4 text-[12px] text-white/45 sm:px-8">
          <span>© {new Date().getFullYear()} Expert Column AI</span>
          <span>招待制のサービスです。ログインには二段階認証が必要です。</span>
        </div>
      </div>
    </footer>
  );
}
